import { makeStyles } from '@material-ui/core'
import CalendarTodayOutlined from '@material-ui/icons/CalendarTodayOutlined'
import ClearIcon from '@material-ui/icons/Clear'
import {
  DatePicker,
  DatePickerProps,
  IconButton,
  OsDateTimePickerVersion,
  styled,
  Tooltip,
  useComponentVersion,
} from 'opensolar-ui'
import PropTypes from 'prop-types'
import { FieldTitle, useInput, useLocale, useTranslate } from 'react-admin'
import { getDateFormat, parseDateObject, parseInputValue } from 'util/date'

export const CalendarGrey = styled(CalendarTodayOutlined, { name: 'CalendarGrey' })(({ theme }) => ({
  height: 16,
  fill: theme.palette.grey[810],
}))

type StyledDatePickerProps = {
  width?: string | number
}
export const StyledDatePicker = styled(DatePicker, { name: 'DateInputContainer' })<StyledDatePickerProps>(
  ({ width, theme }) => ({
    width,
    '& .OSUI-InputBase-root': {
      width,
    },
  })
)

export const DateInput = ({
  label,
  inputVariant,
  value,
  onChange,
  disablePast = false,
  disableFuture = false,
  margin = 'none',
  format,
  disabled,
  width,
  ...rest
}: DatePickerProps & { width?: string | number }) => {
  const locale = useLocale()
  const dayjsFormat = useComponentVersion('date_picker') === OsDateTimePickerVersion.V2

  // To Do: support more locales
  const defaultFormat = getDateFormat({ locale, long: false, variant: 'date', dayjsFormat })

  return (
    <StyledDatePicker
      width={width}
      variant="inline"
      inputVariant={inputVariant}
      label={label}
      value={value}
      onChange={onChange}
      autoOk
      format={format || defaultFormat}
      margin={margin}
      onError={(validationError) => {
        if (validationError) {
          console.log('Date validation error', validationError)
        }
      }}
      disablePast={disablePast}
      disableFuture={disableFuture}
      disabled={disabled}
      {...rest}
    />
  )
}

const useStyles = makeStyles(
  (theme) => ({
    input: {
      width: theme.spacing(32),
    },
    fullWidth: {
      width: '100%',
    },
  }),
  { name: 'OS-date-input' }
)

const DateFormInput = (props) => {
  const {
    required,
    label,
    options = {},
    source,
    resource,
    helperText,
    margin = 'dense',
    onBlur,
    className,
    onChange,
    onFocus,
    optionsTime,
    labelTime,
    validate,
    hideClearButton,
    inputVariant,
    defaultValue,
    disabled,
    disablePast = false,
    disableFuture = false,
    style = {},
    fullWidth,
    width,
    ...rest
  } = props
  const {
    id,
    input,
    isRequired,
    meta: { error, touched, submitError },
  } = useInput({
    onBlur,
    onChange,
    onFocus,
    resource,
    source,
    validate,
    ...rest,
  })
  const translate = useTranslate()
  const classes = useStyles()

  const handleChange = (date) => {
    input.onChange(parseDateObject(date))
    input.onBlur()
  }

  const clearDate = () => {
    input.onChange(null)
    input.onBlur()
  }
  const isVersion2 = useComponentVersion('date_picker') === OsDateTimePickerVersion.V2
  const value = parseInputValue(input?.value || defaultValue, isVersion2)
  return (
    <>
      <DateInput
        label={
          label ? (
            <FieldTitle label={label} source={source} resource={resource} isRequired={isRequired || required} />
          ) : undefined
        }
        variant="inline"
        value={value}
        disabled={disabled}
        onChange={handleChange}
        className={`${fullWidth ? classes.fullWidth : classes.input} ${className} `}
        disablePast={disablePast}
        inputVariant={inputVariant}
        margin={margin}
        error={touched ? error || submitError : false}
        helperText={touched && (error || submitError) ? error || submitError : undefined}
        width={width}
        InputProps={{
          endAdornment:
            !hideClearButton && input.value ? (
              <Tooltip title={translate('Clear Date')} enterDelay={300}>
                <IconButton
                  disabled={disabled}
                  onClick={clearDate}
                  style={{
                    padding: 5,
                    right: 0,
                    width: 30,
                    height: 30,
                  }}
                >
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <CalendarGrey />
            ),
        }}
        {...options}
      />
    </>
  )
}

DateFormInput.propTypes = {
  label: PropTypes.string,
  options: PropTypes.object,
  resource: PropTypes.string,
  source: PropTypes.string,
  defaultValue: PropTypes.object,
  inputVariant: PropTypes.string,
  style: PropTypes.object,
  width: PropTypes.number,
  parse: PropTypes.func,
  disabled: PropTypes.bool,
  className: PropTypes.string,
  hideClearButton: PropTypes.bool,
  onChange: PropTypes.func,
  validate: PropTypes.func,
  disablePast: PropTypes.bool,
  disableFuture: PropTypes.bool,
}

export default DateFormInput
